/*
 * Mix & Match — product-page bundle slot selection, cart carousel and the
 * product-page "complete your pair" banner.
 *
 * Pre-filled pair model: the 2x1 and 4x2 cards (snippets/product-bundle-offer.liquid,
 * snippets/mix-match-selector.liquid) always start with every row already holding
 * a real variant (the theme's "simplest reliable default" — every row defaults
 * to the product being viewed). There is no empty state to fill in. The two
 * cards are stacked radio options; only the active one expands to show its
 * rows, and the single CTA + savings line live below both cards, never inside
 * either one.
 *
 * Responsibilities:
 *  - keep slot #1 (and, when it changes, every dependent row's option list) in
 *    sync with the main product-page variant swatches;
 *  - re-fill dependent rows (Watch 2 for 2x1; Watches 2-4 for 4x2) when their
 *    own dropdown changes, without ever touching the main gallery;
 *  - show the live "crossed-out total / real price" per card and the one
 *    shared "Risparmi" line, and gate the single CTA on every row of the
 *    active card holding a purchasable variant;
 *  - drive the shared guided "choose your next watch" data, in three places
 *    built from the same eligibility + cart-state source: the cart's inline
 *    carousel, the full bottom-sheet grid, and the product page's "complete
 *    your pair" banner.
 *
 * Selections live on the slot elements themselves (`data-selected-*`), which is
 * what `<bundle-offer>` reads when it builds its single cart/add.js request.
 *
 * Prices are never modified here: the price framing is informational only, the
 * real discount is applied by Shopify.
 */
(function () {
  'use strict';

  // `hosts` (the product-page bundle offer) may be absent — the guided picker
  // below is rendered globally (snippets/cart-drawer.liquid) so it must keep
  // working from the cart page, a collection page, or anywhere else with no
  // bundle-offer host at all.
  var hosts = Array.prototype.slice.call(document.querySelectorAll('[data-mm-host]'));

  // The guided "choose your next watch" modal is shared by the product page,
  // the cart's completion nudge and the cart's Mix & Match carousel — see
  // snippets/mix-match-selector.liquid (mode: 'dialog') and the "Guided
  // picker" section below.
  var picker = document.querySelector('[data-mm-picker]');
  var panel, scrollEl, searchEl, noResultsEl, titleEl, subEl;
  var lastTrigger = null;
  var pickerMode = null; // 'complete' | 'swap'
  // Set only by the 4x2 per-line "Cambia gratis" entry point; null for every
  // 2x1 path, which keeps using findSwapTarget() exactly as before.
  var explicitSwapKey = null;

  if (picker) {
    // Move the dialog to <body> so no ancestor overflow, transform or stacking
    // context on any page can clip it.
    if (picker.parentNode !== document.body) document.body.appendChild(picker);
    panel = picker.querySelector('[data-mm-panel]');
    scrollEl = picker.querySelector('[data-mm-scroll]');
    searchEl = picker.querySelector('[data-mm-search]');
    noResultsEl = picker.querySelector('[data-mm-no-results]');
    titleEl = picker.querySelector('[data-mm-title]');
    subEl = picker.querySelector('[data-mm-sub]');
  }

  // The cart drawer (always present, rendered globally) carries the Ordine
  // Prioritario add-on's identifiers — read once so the swap/eligibility
  // logic below can tell a real eligible watch apart from that add-on
  // without depending on cart-drawer.js having run first.
  var cartDrawerRoot = document.querySelector('[data-cart-drawer]');
  var priorityHandle = cartDrawerRoot ? (cartDrawerRoot.getAttribute('data-priority-handle') || '') : '';
  var priorityId = cartDrawerRoot ? (cartDrawerRoot.getAttribute('data-priority-id') || '') : '';

  /* ---------------- Money ---------------- */
  // Standard Shopify money formatting, driven by the shop's own money_format.
  function formatMoney(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return format;

    function number(value, precision, thousands, decimal) {
      if (isNaN(value) || value == null) return '0';
      value = (value / 100.0).toFixed(precision);
      var parts = value.split('.');
      var units = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return units + (parts[1] ? decimal + parts[1] : '');
    }

    var value;
    switch (match[1]) {
      case 'amount': value = number(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = number(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = number(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = number(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = number(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = number(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = number(cents, 0, ' ', ','); break;
      default: value = number(cents, 2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  /* ---------------- Helpers ---------------- */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Mirrors snippets/product-title.liquid. Safe: the title is escaped first,
  // so only our own <sup> markup is ever injected.
  function formatTitle(str) {
    return escapeHtml(str).replace(/®/g, '<sup class="product-title__registered">®</sup>');
  }

  function blocksOf(host) {
    return Array.prototype.slice.call(host.querySelectorAll('[data-mm-block]'));
  }

  function slotsOf(block) {
    return Array.prototype.slice.call(block.querySelectorAll('[data-mm-slot]'));
  }

  function activeOffer(host) {
    var checked = host.querySelector('[data-offer-radio]:checked');
    return checked ? checked.value : '2';
  }

  /* ---------------- Slot state ---------------- */
  function readSlot(slot) {
    var id = slot.getAttribute('data-selected-id') || '';
    if (!id) return null;
    return {
      id: id,
      price: parseInt(slot.getAttribute('data-selected-price'), 10) || 0,
      available: slot.getAttribute('data-selected-available') !== 'false'
    };
  }

  // Central slot renderer: the one place every watch slot (1-4) is filled, so
  // the id, price and availability always update together. `data` comes from
  // the shared variant map. This never touches the main product gallery —
  // only slot #1's own write-back to the main swatches does that.
  function fillSlot(slot, data) {
    slot.setAttribute('data-selected-id', data.id);
    slot.setAttribute('data-selected-price', String(data.price));
    slot.setAttribute('data-selected-available', data.available ? 'true' : 'false');
    slot.classList.toggle('mm-slot--invalid', !data.available);

    var free = slot.querySelector('[data-mm-free]');
    if (free) free.hidden = !data.available;
  }

  /* ---------------- Price framing + CTA gate ---------------- */
  // 2x1: pay the dearest, save the cheapest.
  // 4x2: pay the two dearest, save the two cheapest.
  function split(prices, freeCount) {
    var sorted = prices.slice().sort(function (a, b) { return b - a; });
    var pay = 0;
    var save = 0;
    for (var i = 0; i < sorted.length; i++) {
      if (i < sorted.length - freeCount) pay += sorted[i];
      else save += sorted[i];
    }
    return { pay: pay, save: save };
  }

  function slotsOk(block) {
    return slotsOf(block).every(function (s) {
      return !!s.getAttribute('data-selected-id') && s.getAttribute('data-selected-available') !== 'false';
    });
  }

  // Live "crossed-out combined value / real price" framing for one card,
  // driven entirely by the actual selected slot prices — never simulated: if
  // a variant becomes unavailable the server-rendered defaults are left alone.
  // Returns the { pay, save } split so refresh() can feed the ONE shared
  // "Risparmi" line (below both cards) from whichever card is active.
  function updatePriceFraming(host, block) {
    if (!slotsOk(block)) return null;

    var entries = slotsOf(block).map(readSlot);
    var offer = block.getAttribute('data-offer');
    var card = block.closest('[data-offer-card]');
    var format = host.getAttribute('data-money-format') || '{{amount}}';
    var nowEl = card ? card.querySelector('[data-price-' + offer + ']') : null;
    var wasEl = card ? card.querySelector('[data-price-' + offer + '-was]') : null;

    var combined = entries.reduce(function (sum, e) { return sum + e.price; }, 0);
    var result = split(entries.map(function (e) { return e.price; }), entries.length === 4 ? 2 : 1);

    if (nowEl) nowEl.textContent = formatMoney(result.pay, format);
    if (wasEl) wasEl.textContent = formatMoney(combined, format);
    return result;
  }

  function refresh(host) {
    var offer = activeOffer(host);
    var activeBlock = null;
    var activeResult = null;
    blocksOf(host).forEach(function (block) {
      var result = updatePriceFraming(host, block);
      if (block.getAttribute('data-offer') === String(offer)) {
        activeBlock = block;
        activeResult = result;
      }
    });

    if (!activeBlock) return;

    var atc = host.querySelector('[data-bundle-atc]');
    if (atc) atc.disabled = !slotsOk(activeBlock);

    // The single shared "Risparmi X" line lives below both cards (never
    // inside either one) so it always reflects whichever offer is selected.
    var savingsEl = host.querySelector('[data-bundle-savings]');
    if (savingsEl && activeResult) {
      var format = host.getAttribute('data-money-format') || '{{amount}}';
      var savingsLabel = host.getAttribute('data-savings-label') || 'Risparmi';
      savingsEl.textContent = savingsLabel + ' ' + formatMoney(activeResult.save, format);
    }
  }

  /* ---------------- Slot #1 follows the main variant ---------------- */
  function currentVariants(host) {
    if (host._mmVariants) return host._mmVariants;
    var src = host.querySelector('[data-bundle-prices]');
    try {
      host._mmVariants = JSON.parse(src.textContent);
    } catch (e) {
      host._mmVariants = {};
    }
    return host._mmVariants;
  }

  function fillFromVariant(host, slot, variantId) {
    var info = currentVariants(host)[String(variantId)];
    if (!info || !slot) return false;
    fillSlot(slot, { id: variantId, price: info.price, available: info.available });
    return true;
  }

  function slotAt(block, index) {
    return block.querySelector('[data-mm-slot][data-index="' + index + '"]');
  }

  // Rebuilds a dependent row's <select> to match slot 1's own option list (so
  // every row always agrees on what's purchasable), keeping the customer's
  // own choice if it is still a real, valid option — otherwise defaulting to
  // the new anchor colour. Never left empty.
  function rebuildDependent(anchorSelect, dependentSelect, anchorValue) {
    if (!anchorSelect || !dependentSelect) return;

    var keep = dependentSelect.value;
    dependentSelect.innerHTML = '';
    var frag = document.createDocumentFragment();
    Array.prototype.slice.call(anchorSelect.options).forEach(function (o) {
      var opt = document.createElement('option');
      opt.value = o.value;
      opt.textContent = o.textContent.trim();
      opt.disabled = o.disabled;
      frag.appendChild(opt);
    });
    dependentSelect.appendChild(frag);

    var stillThere = Array.prototype.some.call(dependentSelect.options, function (o) {
      return o.value === keep && !o.disabled;
    });
    dependentSelect.value = stillThere ? keep : anchorValue;
  }

  // A product with a single, default variant renders no <select> at all (see
  // mix-match-slot.liquid), so `dep` can be null here — the slot's own
  // data-selected-* already holds the right (only possible) value in that
  // case, so there is nothing further to sync.
  function syncDependents(host, anchorSelect, dependentSelects, variantId) {
    dependentSelects.forEach(function (dep) {
      if (!dep) return;
      rebuildDependent(anchorSelect, dep, String(variantId));
      fillFromVariant(host, dep.closest('[data-mm-slot]'), dep.value);
    });
  }

  // The gallery's own sync contract: the `gallery:select` event that
  // assets/product.js already fires when the main swatches change — i.e. the
  // exact mechanism dropdown #1 has always used to drive the gallery, reached
  // here directly instead of via the swatch write-back. The gallery listener
  // owns picking the image AND the active thumbnail, so nothing about that is
  // reimplemented. Deliberately gallery-only: routing the non-anchor rows
  // through writeBackToMainSwatches would also re-run syncAnchor and rewrite
  // row #1's variant, changing what gets added to the cart.
  function syncGalleryTo(host, variantId) {
    var info = currentVariants(host)[String(variantId)];
    if (!info || info.mediaId == null) return;
    var gallery = document.querySelector('[data-gallery]');
    if (!gallery) return;
    gallery.dispatchEvent(new CustomEvent('gallery:select', { detail: { mediaId: info.mediaId } }));
  }

  // True only while a select's own change is being written back to the main
  // swatches. syncAnchor uses it to tell a REAL swatch click (which sets the
  // whole card to that colour) from this echo (which must leave the other
  // rows alone, so changing one select never moves the other).
  var writingBack = false;

  function writeBackToMainSwatches(host, variantId) {
    var form = host.closest('product-form') || document;
    var radio = form.querySelector('input[data-variant-id][value="' + variantId + '"]');
    if (radio && !radio.checked) {
      radio.checked = true;
      writingBack = true;
      try {
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        writingBack = false;
      }
    }
  }

  // Point the dependent rows at the same variant and let them fire a REAL
  // `change`, so the ordinary per-select path runs for each one (slot fill,
  // price, gallery, add-to-cart payload) — no parallel state, no second copy
  // of the logic. Rows already on that colour are skipped, so nothing
  // re-dispatches for no reason.
  function forceDependentsTo(dependents, variantId) {
    var wanted = String(variantId);
    dependents.forEach(function (dep) {
      if (!dep || dep.value === wanted) return;
      var selectable = Array.prototype.some.call(dep.options, function (o) {
        return o.value === wanted && !o.disabled;
      });
      if (!selectable) return;
      dep.value = wanted;
      dep.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // `syncMain` writes back to the main product-page swatches, which lets
  // product.js swap the gallery image, price and ?variant= URL as usual.
  // Rows 2+ never touch the gallery — only slot 1 does.
  function applyWatch1(host, block, variantId, syncMain) {
    fillFromVariant(host, slotAt(block, 1), variantId);
    syncDependents(host, block.querySelector('[data-mm-w1]'), [block.querySelector('[data-mm-w2]')], variantId);
    if (syncMain) writeBackToMainSwatches(host, variantId);
  }

  function applyWatch2(host, block) {
    var w2 = block.querySelector('[data-mm-w2]');
    var slot = slotAt(block, 2);
    if (!w2 || !slot) return;
    fillFromVariant(host, slot, w2.value);
  }

  function applyQuadAnchor(host, block, variantId, syncMain) {
    fillFromVariant(host, slotAt(block, 1), variantId);
    var deps = Array.prototype.slice.call(block.querySelectorAll('[data-mm-qselect]'));
    syncDependents(host, block.querySelector('[data-mm-qw1]'), deps, variantId);
    if (syncMain) writeBackToMainSwatches(host, variantId);
  }

  // Slot 1's own local dropdown (mirrors the main swatches) drives the main
  // product-page variant too, so product.js swaps the gallery as usual.
  function quadSyncFromSelf(host, block, variantId) {
    fillFromVariant(host, slotAt(block, 1), variantId);
    writeBackToMainSwatches(host, variantId);
  }

  function syncAnchor(host, variantId) {
    // A swatch click sets the WHOLE card to that colour (every row), while the
    // echo of a select's own write-back only re-anchors row #1.
    var fromSwatch = !writingBack;
    blocksOf(host).forEach(function (block) {
      if (block.hasAttribute('data-mm-duo')) {
        var w1 = block.querySelector('[data-mm-w1]');
        if (w1 && w1.value !== String(variantId)) w1.value = String(variantId);
        applyWatch1(host, block, variantId, false);
        if (fromSwatch) forceDependentsTo([block.querySelector('[data-mm-w2]')], variantId);
      } else if (block.hasAttribute('data-mm-quad')) {
        var qw1 = block.querySelector('[data-mm-qw1]');
        if (qw1 && qw1.value !== String(variantId)) qw1.value = String(variantId);
        applyQuadAnchor(host, block, variantId, false);
        if (fromSwatch) {
          forceDependentsTo(Array.prototype.slice.call(block.querySelectorAll('[data-mm-qselect]')), variantId);
        }
      }
    });
    refresh(host);
  }

  /* ---------------- Guided picker ("choose your next watch" modal) ----------
   * Shared by the product page's cart-completion continuation and the cart's
   * carousel/"Vedi tutti i modelli" link. It is pure presentation: what to
   * show and how each add behaves come entirely from the shared cart state
   * and, for swaps, from assets/product-card.js reading window.Velare.mixMatchSwap.
   * ------------------------------------------------------------------------ */
  function focusable() {
    return Array.prototype.slice
      .call(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter(function (el) { return el.offsetParent !== null; });
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeGuidedPicker();
      return;
    }
    if (e.key !== 'Tab' || !panel) return;
    var f = focusable();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // What the cart still needs, from the SAME state cart-drawer.js already
  // publishes. The 4x2 marker takes priority over the plain odd/even count so
  // a 4x2 in progress is never mistaken for a half-open 2x1 — adding the first
  // of four watches also makes the cart's total eligible count odd.
  function pendingProgress() {
    // ---- 4x2, derived from the cart itself (no parallel state system) ----
    // Three eligible watches is always a 4x2 one short, and four is a complete
    // 4x2 with nothing left to add. Every other count falls straight through to
    // the original 2x1 logic below, which is left exactly as it was:
    //   0 -> null, 1 -> duo missing 1, 2 -> null (pair, handled as swap).
    // The session marker still drives the 1-2 counts of an in-progress 4x2,
    // unchanged.
    var stateCart = window.Velare && window.Velare.bundleState && window.Velare.bundleState.cart;
    var eligible = eligibleCount(stateCart);
    if (eligible === 3) {
      return { mode: 'quad', missing: 1 };
    }
    if (eligible === 4) {
      return null;
    }

    var marker = quadMarker();
    if (marker === '4' && quadInCart > 0 && quadInCart < 4) {
      return { mode: 'quad', missing: 4 - quadInCart };
    }
    if (cartPending) {
      return { mode: 'duo', missing: 1 };
    }
    return null;
  }

  function renderGuidedHead(progress) {
    if (progress.mode === 'quad') {
      if (titleEl) titleEl.textContent = picker.getAttribute('data-title-quad') || '';
      if (subEl) subEl.textContent = picker.getAttribute('data-sub-quad-' + progress.missing) || '';
    } else {
      if (titleEl) titleEl.textContent = picker.getAttribute('data-title-duo') || '';
      if (subEl) subEl.textContent = picker.getAttribute('data-sub-duo') || '';
    }
  }

  function renderSwapHead() {
    if (titleEl) titleEl.textContent = picker.getAttribute('data-title-swap') || '';
    if (subEl) subEl.textContent = picker.getAttribute('data-sub-swap') || '';
  }

  // The pair-state "Vedi tutti i modelli" head: the "Abbina come vuoi" title,
  // no subtitle (the full grid does the same plain add — a model that's trimmed
  // afterwards — not a swap).
  function renderAddHead() {
    if (titleEl) titleEl.textContent = picker.getAttribute('data-title-pairadd') || '';
    if (subEl) subEl.textContent = '';
  }

  function filterGrid(query) {
    var items = Array.prototype.slice.call(picker.querySelectorAll('[data-mm-item]'));
    var needle = (query || '').trim().toLowerCase();
    var visible = 0;
    items.forEach(function (item) {
      var match = !needle || (item.getAttribute('data-search') || '').indexOf(needle) !== -1;
      item.hidden = !match;
      if (match) visible += 1;
    });
    if (noResultsEl) noResultsEl.hidden = visible !== 0;
  }

  // Every grid card's button reflects the picker's own explicit mode —
  // "SOSTITUISCI" in swap mode, the normal add label otherwise — the same
  // pairing the carousel already drives via data-cta-swap/data-cta-add
  // (cart.carousel.cta_swap/cta_add), reused here rather than a new label.
  // Applied to every item (not just visible ones) so it's correct even
  // before any search filtering.
  function pickerButtonLabels() {
    if (!picker) return;
    var label = pickerMode === 'swap' ? picker.getAttribute('data-cta-swap') : picker.getAttribute('data-cta-add');
    if (!label) return;
    Array.prototype.slice.call(picker.querySelectorAll('[data-mm-item] [data-card-atc]')).forEach(function (btn) {
      btn.textContent = label;
    });
  }

  function showPickerShell(trigger) {
    lastTrigger = trigger || document.activeElement;
    if (searchEl) searchEl.value = '';
    filterGrid('');
    pickerButtonLabels();

    picker.hidden = false;
    document.body.classList.add('mm-picker-open');
    document.addEventListener('keydown', onKeydown);
    if (scrollEl) scrollEl.scrollTop = 0;

    var target = (searchEl && searchEl.offsetParent !== null) ? searchEl : picker.querySelector('[data-mm-close]');
    if (target) target.focus();
  }

  // Opens the modal when the cart still needs more eligible watches for the
  // active 2x1/4x2. Returns whether it actually opened, so callers know
  // whether to fall back to just showing the cart instead.
  function openGuidedPicker(trigger) {
    if (!picker) return false;
    var progress = pendingProgress();
    if (!progress) return false;

    pickerMode = 'complete';
    window.Velare.mixMatchSwap = null;
    renderGuidedHead(progress);
    showPickerShell(trigger);
    return true;
  }

  // Opens the modal so the customer can replace the free watch in an already
  // complete cart. `findSwapTarget()` is only checked here as a guard (is
  // there anything eligible to swap at all) — the actual target used by the
  // add is computed FRESH at the moment of the click (see the capture-phase
  // listener below), exactly like the carousel already does, so it can never
  // go stale while the picker sits open (e.g. while the customer searches).
  function openSwapPicker(trigger) {
    if (!picker) return false;
    if (!findSwapTarget()) return false;

    pickerMode = 'swap';
    explicitSwapKey = null;
    window.Velare.mixMatchSwap = null;
    renderSwapHead();
    showPickerShell(trigger);
    return true;
  }

  // The pair-state carousel's "Vedi tutti i modelli": the full grid, opened in
  // plain-add mode. No swap target is armed, so the chosen model is simply
  // added as a third watch — the customer then removes the one they don't want
  // (the same add-then-trim flow the inline carousel now uses).
  function openAddPicker(trigger) {
    if (!picker) return false;
    pickerMode = 'add';
    explicitSwapKey = null;
    window.Velare.mixMatchSwap = null;
    renderAddHead();
    showPickerShell(trigger);
    return true;
  }

  // 4x2 only: the same swap picker, but pinned to ONE specific cart line
  // (the "Cambia gratis" link under a free watch) instead of letting
  // findSwapTarget() pick the cheapest. Everything downstream — the picker,
  // the capture-phase arming below, product-card-form's runSwap — is the
  // shared, unmodified routine; only which line it targets differs.
  function swapTargetForKey(key) {
    var cart = window.Velare && window.Velare.bundleState && window.Velare.bundleState.cart;
    if (!cart || !cart.items || !key) return null;
    var found = null;
    cart.items.forEach(function (item) {
      if (found) return;
      if (item.key === key && isEligibleCartItem(item)) found = item;
    });
    if (!found) return null;
    // changeFirst: decrement this line BEFORE adding the replacement. Adding
    // first would take the cart to five watches, and Shopify re-allocating the
    // 4x2 discount at that moment can split a merged "x2" line into two new
    // lines — invalidating this key. See runSwap in snippets/product-card.liquid.
    return { key: found.key, nextQuantity: found.quantity - 1, variantId: found.variant_id, changeFirst: true };
  }

  function openSwapPickerFor(key, trigger) {
    if (!picker) return false;
    if (!swapTargetForKey(key)) return false;

    pickerMode = 'swap';
    explicitSwapKey = key;
    window.Velare.mixMatchSwap = null;
    renderSwapHead();
    showPickerShell(trigger);
    return true;
  }

  function closeGuidedPicker() {
    if (!picker || picker.hidden) return;
    picker.hidden = true;
    pickerMode = null;
    explicitSwapKey = null;
    window.Velare.mixMatchSwap = null;
    document.body.classList.remove('mm-picker-open');
    document.removeEventListener('keydown', onKeydown);
    if (lastTrigger && typeof lastTrigger.focus === 'function' && lastTrigger.offsetParent !== null) {
      lastTrigger.focus();
    }
    lastTrigger = null;
  }

  if (picker) {
    // Capture phase, so this always runs BEFORE product-card-form's own
    // bubble-phase click handler on the very same button — same pattern the
    // carousel uses below, reusing the exact same findSwapTarget(), so the
    // swap add always sees a fresh target (never one computed back when the
    // picker first opened, which could go stale while it sits open).
    picker.addEventListener('click', function (e) {
      var atc = e.target.closest('[data-card-atc]');
      if (!atc) return;
      if (pickerMode !== 'swap') {
        window.Velare.mixMatchSwap = null;
        return;
      }
      // Pinned to one line for 4x2's per-line links; otherwise the original
      // cheapest-line target, unchanged.
      window.Velare.mixMatchSwap = explicitSwapKey ? swapTargetForKey(explicitSwapKey) : findSwapTarget();
    }, true);

    picker.addEventListener('click', function (e) {
      if (e.target.closest('[data-mm-close]') || e.target.closest('[data-mm-overlay]')) {
        closeGuidedPicker();
      }
    });

    if (searchEl) {
      searchEl.addEventListener('input', function () {
        filterGrid(searchEl.value);
      });
    }
  }

  // Any trigger anywhere on the page opens the same shared modal — a single
  // delegated listener, so no page ever wires its own copy:
  //  - [data-mm-guided-trigger] — the /cart page's no-JS-safe fallback
  //    (something is missing);
  //  - [data-mm-view-all] — the carousel's "Vedi tutti i modelli" link, opened
  //    in whichever purpose (complete/swap) the carousel is currently in.
  document.addEventListener('click', function (e) {
    var completeTrigger = e.target.closest('[data-mm-guided-trigger]');
    if (completeTrigger) {
      e.preventDefault();
      if (!openGuidedPicker(completeTrigger)) {
        var toggle = document.querySelector('[data-cart-toggle]');
        if (toggle) toggle.click();
      }
      return;
    }
    var viewAll = e.target.closest('[data-mm-view-all]');
    if (viewAll) {
      e.preventDefault();
      // Both add-then-remove states (2x1 pair, complete 4x2) open the full grid
      // in plain-add mode (the chosen model just joins the cart); an incomplete
      // bundle opens the guided "complete your bundle" picker exactly as before.
      var isAddState = carouselMode === 'pairadd' || carouselMode === 'quadadd';
      var opened = isAddState ? openAddPicker(viewAll) : openGuidedPicker(viewAll);
      if (!opened) {
        var toggle2 = document.querySelector('[data-cart-toggle]');
        if (toggle2) toggle2.click();
      }
    }
  });

  /* ---------------- Host wiring ---------------- */
  hosts.forEach(function (host) {
    host.addEventListener('change', function (e) {
      // Switching offer card (2x1 <-> 4x2) re-evaluates the CTA gate.
      if (e.target.matches('[data-offer-radio]')) {
        refresh(host);
        return;
      }
      var block = e.target.closest('[data-mm-duo]');
      if (block) {
        if (e.target.matches('[data-mm-w1]')) {
          // Watch 1 = the real product variant: its own write-back to the main
          // swatches already previews it in the gallery.
          applyWatch1(host, block, e.target.value, true);
        } else if (e.target.matches('[data-mm-w2]')) {
          // Row #2 changes only its own selected-id/price/availability, but
          // now also previews its colour in the gallery — last dropdown
          // touched wins, without touching row #1's selection.
          applyWatch2(host, block);
          syncGalleryTo(host, e.target.value);
        }
        refresh(host);
        return;
      }
      var quad = e.target.closest('[data-mm-quad]');
      if (!quad) return;
      if (e.target.matches('[data-mm-qw1]')) {
        quadSyncFromSelf(host, quad, e.target.value);
      } else if (e.target.matches('[data-mm-qselect]')) {
        // Rows #2-#4: same gallery preview as row #1 gets, selection untouched.
        fillFromVariant(host, e.target.closest('[data-mm-slot]'), e.target.value);
        syncGalleryTo(host, e.target.value);
      }
      refresh(host);
    });

    // Slot #1 always mirrors the main product-page variant.
    var form = host.closest('product-form') || document;
    form.addEventListener('change', function (e) {
      if (e.target.matches && e.target.matches('[data-variant-id]')) {
        syncAnchor(host, e.target.value);
      }
    });

    // The server already renders every row pre-filled and in sync; this just
    // keeps the dependent rows' slot data consistent with their dropdowns.
    blocksOf(host).forEach(function (block) {
      if (block.hasAttribute('data-mm-duo')) applyWatch2(host, block);
      if (block.hasAttribute('data-mm-quad')) {
        Array.prototype.slice.call(block.querySelectorAll('[data-mm-qselect]')).forEach(function (dep) {
          fillFromVariant(host, dep.closest('[data-mm-slot]'), dep.value);
        });
      }
    });

    refresh(host);
  });

  /* ---------------- Eligibility (shared by swap, carousel, banner) ---------- */
  function isEligibleCartItem(item) {
    if (priorityHandle && item.handle === priorityHandle) return false;
    if (priorityId && String(item.variant_id) === String(priorityId)) return false;
    if (item.gift_card) return false;
    return true;
  }

  function eligibleCount(cart) {
    var count = 0;
    if (!cart || !cart.items) return count;
    cart.items.forEach(function (item) {
      if (isEligibleCartItem(item)) count += item.quantity;
    });
    return count;
  }

  // True once every eligible cart item shares the same product handle. A
  // complete pair/quad that's still one handle is the "simplest reliable
  // default" (both/all rows never touched) — still worth offering a swap.
  // Once it's more than one distinct handle, the customer has already used
  // the swap once; offering it again is what the module-visibility rule
  // below hides.
  function isSameModelPair(cart) {
    var handles = {};
    if (!cart || !cart.items) return true;
    cart.items.forEach(function (item) {
      if (isEligibleCartItem(item)) handles[item.handle] = true;
    });
    return Object.keys(handles).length <= 1;
  }

  // The unit-cheapest eligible line is always the one Shopify's automatic
  // discount frees (2x1: the only other one; 4x2: one of the two cheapest) —
  // swapping it can never touch a paid watch. If that line's quantity is 2+
  // (e.g. the customer left both pre-filled rows on the same colour, merged
  // by <bundle-offer> into one line), only one unit is released, so the paid
  // unit on that same line is never removed.
  function findSwapTarget() {
    var cart = window.Velare && window.Velare.bundleState && window.Velare.bundleState.cart;
    if (!cart || !cart.items) return null;
    var cheapest = null;
    cart.items.forEach(function (item) {
      if (!isEligibleCartItem(item)) return;
      if (!cheapest || item.price < cheapest.price) cheapest = item;
    });
    if (!cheapest) return null;
    // variantId lets assets/product-card.js recognise a no-op "swap" (the
    // customer re-picks the exact watch being replaced) and just close
    // instead of adding one unit and immediately removing it again.
    return { key: cheapest.key, nextQuantity: cheapest.quantity - 1, variantId: cheapest.variant_id };
  }

  // The "paid watch" reference for the carousel's GRATIS badge / price
  // preview (display-only — see updateCarouselItemStatus): the most
  // expensive currently-eligible cart item, i.e. the one guaranteed to stay
  // paid no matter which cheaper item gets swapped. In the incomplete-pair
  // state there's only one eligible item yet, so it's simply that one —
  // same function, same logic, both states.
  function paidWatchInfo() {
    var cart = window.Velare && window.Velare.bundleState && window.Velare.bundleState.cart;
    if (!cart || !cart.items) return null;
    var best = null;
    cart.items.forEach(function (item) {
      if (!isEligibleCartItem(item)) return;
      if (!best || item.price > best.price) best = item;
    });
    if (!best) return null;
    return { price: best.price, title: best.product_title };
  }

  /* ---------------- Cart carousel (Mix & Match) ------------------------------
   * Inline, always-visible-when-relevant widget living in the cart drawer body.
   * One shared component, states derived purely from the live cart state (never
   * a manual "open" step, never a second state system):
   *   - "add"      — an incomplete bundle: pick one and it's added as the
   *     missing watch (shown above the items).
   *   - "pairadd"  — a complete 2x1 pair: pick another and it's ADDED as a
   *     third watch (above the items); the customer then trims one.
   *   - "quadadd"  — a complete 4x2: same add-then-remove, shown BELOW the
   *     items so the four watches are reviewed first.
   * Every item reuses the theme's own product-card + product-card-form, so the
   * real add logic is never duplicated — this widget only decides what to show,
   * where, and always CLEARS window.Velare.mixMatchSwap (it never swaps now), in
   * a capture-phase listener that runs before product-card-form's own click
   * handler, so a plain add is what happens and nothing leaks elsewhere.
   * ------------------------------------------------------------------------ */
  var carousel = document.querySelector('[data-mm-carousel]');
  var carouselTrack, carouselTitleEl, carouselSubEl, carouselEmptyEl, carouselPrevBtn, carouselNextBtn;
  var carouselIndex = 0;
  var carouselMode = null; // 'add' | 'pairadd' | 'quadadd' | null
  var carouselMoneyFormat = '{{amount}}';

  if (carousel) {
    carouselTrack = carousel.querySelector('[data-mm-carousel-track]');
    carouselTitleEl = carousel.querySelector('[data-mm-carousel-title]');
    carouselSubEl = carousel.querySelector('[data-mm-carousel-sub]');
    carouselEmptyEl = carousel.querySelector('[data-mm-carousel-empty]');
    carouselPrevBtn = carousel.querySelector('[data-mm-carousel-prev]');
    carouselNextBtn = carousel.querySelector('[data-mm-carousel-next]');
    carouselMoneyFormat = carousel.getAttribute('data-money-format') || carouselMoneyFormat;

    // The carousel's DOM position is FIXED by the cart-drawer template: it is
    // rendered inline, always directly below the line items, in every state.
    // This script never moves it (no slot juggling), so it can never drift
    // above the items on a re-render — updateCarousel only toggles its
    // visibility and content.
  }

  function carouselItems() {
    return carousel ? Array.prototype.slice.call(carousel.querySelectorAll('[data-mm-carousel-item]')) : [];
  }

  // Every eligible product not already in the cart, in the server-rendered
  // (review-count-descending, ties fall back to collection order) order.
  function carouselCandidates() {
    var cart = window.Velare && window.Velare.bundleState && window.Velare.bundleState.cart;
    var inCart = {};
    if (cart && cart.items) {
      cart.items.forEach(function (item) {
        if (isEligibleCartItem(item)) inCart[item.handle] = true;
      });
    }
    return carouselItems().filter(function (item) {
      return !inCart[item.getAttribute('data-product-handle')];
    });
  }

  function renderCarouselHead() {
    if (!carousel) return;
    // Both add-then-remove states (2x1 pair and complete 4x2) share the same
    // "Abbina come vuoi" head — title only, no helper subtitle.
    if (carouselMode === 'pairadd' || carouselMode === 'quadadd') {
      if (carouselTitleEl) carouselTitleEl.textContent = carousel.getAttribute('data-title-swap') || '';
      if (carouselSubEl) carouselSubEl.hidden = true;
      return;
    }
    var progress = pendingProgress();
    var quadMissing = progress && progress.mode === 'quad' ? progress.missing : 0;
    var title = quadMissing > 0
      ? carousel.getAttribute('data-title-add-4x2-' + quadMissing)
      : carousel.getAttribute('data-title-add');
    if (carouselTitleEl) carouselTitleEl.textContent = title || '';
    if (carouselSubEl) carouselSubEl.hidden = true;
  }

  function carouselButtonLabel(item) {
    if (!carousel || !item) return;
    var atc = item.querySelector('[data-card-atc]');
    if (!atc) return;
    // Both carousel states now add (the pair state adds a third watch the
    // customer then trims, rather than swapping) — always the AGGIUNGI label.
    var label = carousel.getAttribute('data-cta-add');
    if (label) atc.textContent = label;
  }

  // Real price of the browsed item's currently selected variant, in cents —
  // read from the same data the variant <select> already carries
  // (snippets/product-card.liquid's data-price-cents), falling back to the
  // card's default variant price when it has no colour choice at all.
  // Display-only: nothing here changes what gets added or removed.
  function browsedPriceCents(item) {
    if (!item) return null;
    var select = item.querySelector('[data-card-variant]');
    if (select && select.selectedIndex > -1) {
      var opt = select.options[select.selectedIndex];
      var raw = opt && opt.getAttribute('data-price-cents');
      if (raw != null && raw !== '') return parseInt(raw, 10);
    }
    var form = item.querySelector('product-card-form');
    var fallback = form && form.getAttribute('data-default-price-cents');
    return fallback != null && fallback !== '' ? parseInt(fallback, 10) : null;
  }

  function browsedTitle(item) {
    if (!item) return '';
    var titleEl = item.querySelector('.product-card__title');
    return titleEl ? titleEl.textContent.trim() : '';
  }

  // Warns before a checkout surprise, only when there actually is one:
  // compares the browsed variant's real price against the watch already
  // kept in the cart (paidWatchInfo — same reference for both the swap and
  // incomplete-pair states). Shown ONLY when the browsed model costs MORE
  // than the paid watch (it would become the new paid one, freeing the
  // watch that's paid today) — same price or cheaper needs no warning, so
  // the card stays minimal in the common case. Lives inside the currently
  // visible item's own card (data-card-preview, see
  // snippets/product-card.liquid), not a shared element, since each card is
  // now a fully self-contained white band. Display-only; Shopify computes
  // the actual discount after the swap happens.
  // Every browsed model communicates its status in exactly one of two ways,
  // never both: a "GRATIS" badge next to the price when it costs the same
  // or less than the paid watch (it would become the free one, no surprise
  // to warn about), or the price-preview line when it costs more (it would
  // become the new paid watch, freeing the one that's paid today).
  function updateCarouselItemStatus() {
    var active = carouselItems().filter(function (item) { return !item.hidden; })[0];
    if (!active) return;
    var previewEl = active.querySelector('[data-card-preview]');
    var badgeEl = active.querySelector('[data-card-free-badge]');

    var paid = paidWatchInfo();
    var browsedCents = browsedPriceCents(active);

    if (!paid || browsedCents == null) {
      if (previewEl) previewEl.hidden = true;
      if (badgeEl) badgeEl.hidden = true;
      return;
    }

    if (browsedCents > paid.price) {
      if (badgeEl) badgeEl.hidden = true;
      if (previewEl) {
        // Only the price figure carries weight (semibold via <strong>,
        // styled in assets/mix-match.css) — the surrounding template text
        // and the watch name stay regular weight, so the box never
        // competes with the button. The template itself is trusted
        // (theme/merchant copy), but it and the product name are both
        // escaped before going into innerHTML, same discipline as
        // assets/cart-drawer.js's own HTML building.
        var moreTemplate = carousel.getAttribute('data-preview-more') || '';
        var priceHtml = '<strong>' + escapeHtml(formatMoney(browsedCents, carouselMoneyFormat)) + '</strong>';
        previewEl.innerHTML = escapeHtml(moreTemplate)
          .replace('{price}', priceHtml)
          .replace('{name}', escapeHtml(paid.title));
        previewEl.hidden = false;
      }
    } else {
      if (previewEl) previewEl.hidden = true;
      if (badgeEl) badgeEl.hidden = false;
    }
  }

  function showCarouselItem(candidates) {
    carouselItems().forEach(function (item) { item.hidden = true; });

    if (!candidates.length) {
      if (carouselEmptyEl) carouselEmptyEl.hidden = false;
      if (carouselTrack) carouselTrack.hidden = true;
      if (carouselPrevBtn) carouselPrevBtn.hidden = true;
      if (carouselNextBtn) carouselNextBtn.hidden = true;
      return;
    }

    if (carouselEmptyEl) carouselEmptyEl.hidden = true;
    if (carouselTrack) carouselTrack.hidden = false;
    var multi = candidates.length > 1;
    if (carouselPrevBtn) carouselPrevBtn.hidden = !multi;
    if (carouselNextBtn) carouselNextBtn.hidden = !multi;

    if (carouselIndex >= candidates.length || carouselIndex < 0) carouselIndex = 0;
    var active = candidates[carouselIndex];
    active.hidden = false;
    carouselButtonLabel(active);
    updateCarouselItemStatus();
  }

  function updateCarousel() {
    if (!carousel) return;
    var progress = pendingProgress();
    var cart = window.Velare && window.Velare.bundleState && window.Velare.bundleState.cart;

    // Both complete bundles now use the SAME add-then-remove module — one
    // shared "Abbina come vuoi" carousel whose AGGIUNGI button adds the chosen
    // model without removing anything; the customer then trims what they don't
    // want with the line's trash icon and Shopify reallocates the discount.
    //   - 4+ eligible watches -> 'quadadd', shown BELOW the items (four watches
    //     are worth reviewing first). Checked first, so the 5-watch state right
    //     after an AGGIUNGI keeps the same module in the same place instead of
    //     flipping to a "complete your pair" prompt on the odd count.
    //   - an incomplete bundle (1-3, or a partial 4x2 from the session marker)
    //     -> 'add', the plain complete-your-bundle prompt ABOVE the items.
    //   - exactly two same-model eligible watches -> 'pairadd' (2x1), the
    //     add-then-remove module ABOVE the items.
    // Everything else (e.g. two DIFFERENT models — the pair swap already used)
    // -> null, hidden.
    var eligible = eligibleCount(cart);
    if (eligible >= 4) {
      carouselMode = 'quadadd';
    } else if (progress) {
      carouselMode = 'add';
    } else if (eligible === 2 && isSameModelPair(cart)) {
      carouselMode = 'pairadd';
    } else {
      carouselMode = null;
    }

    // Position is fixed by the template (always below the items) — nothing to
    // move here. This function only shows/hides the module and fills it: a
    // single inline element, so it can never stack or land above the items.
    if (!carouselMode) {
      carousel.hidden = true;
      return;
    }

    carousel.hidden = false;
    renderCarouselHead();
    showCarouselItem(carouselCandidates());
  }

  if (carousel) {
    if (carouselPrevBtn) {
      carouselPrevBtn.addEventListener('click', function () {
        var candidates = carouselCandidates();
        if (!candidates.length) return;
        carouselIndex = (carouselIndex - 1 + candidates.length) % candidates.length;
        showCarouselItem(candidates);
      });
    }
    if (carouselNextBtn) {
      carouselNextBtn.addEventListener('click', function () {
        var candidates = carouselCandidates();
        if (!candidates.length) return;
        carouselIndex = (carouselIndex + 1) % candidates.length;
        showCarouselItem(candidates);
      });
    }

    // Capture phase, so this always runs BEFORE product-card-form's own
    // bubble-phase click handler on the very same button. The carousel only
    // ever adds now (the pair state included), so it never arms a swap target
    // — it clears it synchronously, for this one click only, so a plain add is
    // what product-card-form performs.
    carousel.addEventListener('click', function (e) {
      var atc = e.target.closest('[data-card-atc]');
      if (!atc) return;
      window.Velare.mixMatchSwap = null;
    }, true);

    // Picking a different colour changes the browsed variant's price, so the
    // preview line needs to follow it — read-only, doesn't touch
    // product-card-form's own onVariant() image/price-swap behaviour.
    carousel.addEventListener('change', function (e) {
      if (!e.target.closest('[data-card-variant]')) return;
      updateCarouselItemStatus();
    });
  }

  /* ---------------- Product-page "complete your pair" banner --------------
   * Shown above the offer card whenever the cart already holds an incomplete
   * 2x1/4x2 — same pendingProgress() the cart's own nudges use, no second
   * state system. Its button adds THIS product's current variant as the
   * missing watch, then opens the cart so the real discount is visible.
   * ------------------------------------------------------------------------ */
  var promoBanner = document.querySelector('[data-promo-banner]');
  var promoBannerTitleEl = promoBanner ? promoBanner.querySelector('[data-promo-banner-title]') : null;
  var promoBannerCta = promoBanner ? promoBanner.querySelector('[data-promo-banner-cta]') : null;
  var promoBannerBusy = false;

  function updatePromoBanner() {
    if (!promoBanner) return;
    var progress = pendingProgress();
    if (!progress) {
      promoBanner.hidden = true;
      return;
    }
    var title = progress.mode === 'quad'
      ? promoBanner.getAttribute('data-title-4x2-' + progress.missing)
      : promoBanner.getAttribute('data-title-2x1');
    if (promoBannerTitleEl) promoBannerTitleEl.textContent = title || '';
    promoBanner.hidden = false;
  }

  if (promoBannerCta) {
    promoBannerCta.addEventListener('click', function () {
      if (promoBannerBusy) return;
      var variantId = promoBanner.getAttribute('data-variant-id');
      if (!variantId) return;

      promoBannerBusy = true;
      promoBannerCta.disabled = true;
      promoBannerCta.classList.add('is-loading');

      var addUrl = (promoBanner.getAttribute('data-add-url') || '/cart/add') + '.js';
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId) || variantId, quantity: 1 }] })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          return window.Velare && window.Velare.refreshCart ? window.Velare.refreshCart() : null;
        })
        .then(function () {
          var toggle = document.querySelector('[data-cart-toggle]');
          if (toggle) {
            toggle.click();
          } else {
            window.location.href = promoBanner.getAttribute('data-cart-url') || '/cart';
          }
        })
        .catch(function () {
          window.location.href = promoBanner.getAttribute('data-cart-url') || '/cart';
        })
        .finally(function () {
          promoBannerBusy = false;
          promoBannerCta.disabled = false;
          promoBannerCta.classList.remove('is-loading');
        });
    });
  }

  /* ---------------- Subscribe to the shared cart state ---------------- */
  // The 4x2 journey is isolated from the 2x1 by a session marker: the 4x2
  // completing UI only appears while the customer is actually assembling a 4x2
  // (marker === '4') and the cart holds a partial group of eligible watches.
  var cartPending = false;
  var quadInCart = 0;

  function quadMarker() {
    try { return sessionStorage.getItem('velareBundleOffer') || ''; } catch (e) { return ''; }
  }

  function onCartState(detail) {
    var next = !!(detail && detail.pending);
    var wc = detail ? (detail.watchCount || 0) : 0;

    // A clean multiple of four means no partial 4x2 is in progress.
    var marker = quadMarker();
    if (wc % 4 === 0 && marker) {
      try { sessionStorage.removeItem('velareBundleOffer'); } catch (e) {}
      marker = '';
    }
    var q = marker === '4' ? wc % 4 : 0;

    if (next !== cartPending || q !== quadInCart) {
      cartPending = next;
      quadInCart = q;
      hosts.forEach(refresh);
    }

    // The cart carousel is synced (shown/hidden + content) by
    // assets/cart-drawer.js's render() calling window.Velare.syncCartCarousel()
    // as part of the same rebuild that writes the line items — its POSITION is
    // fixed by the template (always below the items), so nothing is moved. This
    // handler only keeps the derived pair/quad state, the product-page banner
    // and the guided modal in sync.
    updatePromoBanner();

    // Keep the guided modal in sync with the live cart while it's open for
    // "complete" purposes: refresh its progress copy, or close it once the
    // pair/quad is complete (the completing add itself already opens the
    // cart). Swap-mode closes itself explicitly once the swap succeeds.
    // Plain add-mode (the pair-state "Vedi tutti i modelli") closes as soon as
    // the chosen model lands, revealing the updated drawer behind it.
    if (picker && !picker.hidden) {
      if (pickerMode === 'complete') {
        var progress = pendingProgress();
        if (progress) {
          renderGuidedHead(progress);
        } else {
          closeGuidedPicker();
        }
      } else if (pickerMode === 'add') {
        closeGuidedPicker();
      }
    }
  }

  document.addEventListener('velare:cart', function (e) { onCartState(e.detail); });

  // cart-drawer.js publishes on load; if it already has, adopt it immediately
  // and sync the carousel for that initial state (subsequent syncs come from
  // cart-drawer.js's render → syncCartCarousel). With no drawer on the page
  // nothing publishes and the offer simply stays in its pre-filled state.
  window.Velare = window.Velare || {};
  if (window.Velare.bundleState) {
    onCartState(window.Velare.bundleState);
    updateCarousel();
  }

  // Sync entry point for assets/cart-drawer.js: refreshes the carousel's
  // visibility and content from the current shared cart state, called as part
  // of the drawer's own render. Position is fixed by the template (always below
  // the items), so this never moves the module.
  window.Velare.syncCartCarousel = updateCarousel;

  // Shared entry points other scripts use:
  //  - openMixMatchPicker() — continue completing the active 2x1/4x2 (returns
  //    false when nothing is missing, so the caller just opens the cart);
  //  - openMixMatchSwap(trigger) — open the swap picker (returns false when
  //    there is nothing eligible to swap);
  //  - closeMixMatchPicker() — used by assets/product-card.js once a swap add
  //    succeeds, so it never has to know about the modal's internals.
  window.Velare.openMixMatchPicker = function () { return openGuidedPicker(); };
  window.Velare.openMixMatchSwap = function (trigger) { return openSwapPicker(trigger); };
  //  - openMixMatchSwapFor(key, trigger) — 4x2 only: same swap picker pinned to
  //    one specific free cart line (assets/cart-drawer.js's "Cambia gratis").
  window.Velare.openMixMatchSwapFor = function (key, trigger) { return openSwapPickerFor(key, trigger); };
  window.Velare.closeMixMatchPicker = closeGuidedPicker;
})();
